import moment, { Moment } from 'moment';
import numeral from 'numeral';
import { GetCurrencySymbol } from '../../../../core/utils/GetCurrenciesData';
import { AUTO_COMMISSION_PAID_TYPE, MANUAL_COMMISSION_PAID_TYPE } from '../../../../core/utils/Constants/Constants';
import { CommissionStatementMetadata } from '../../../../core/types/GenrateMonthStatements';
import { CommissionTabularDataType, CommissionPaymentDetailsType } from '../../../../core/types/CommissionDetailsTypes';

export const convert = (summary: any) => {
  const { stmt_month_dropdown_values } = summary;
  let commissionStatementMonthData: CommissionStatementMetadata[] | [] = stmt_month_dropdown_values as CommissionStatementMetadata[] | [];
  return {
    statements: commissionStatementMonthData
  };
}

const createSummaryEventEntry = (transactions: CommissionTabularDataType, events: any, translate: Function) => {
  transactions.commission_payment_breakdown_summary_version?.forEach((transaction: any) => {
    const entry: any = {
      event: {},
      fields: [],
      commissionType: transaction.ismanualcommission ? MANUAL_COMMISSION_PAID_TYPE : AUTO_COMMISSION_PAID_TYPE,
      commissionViewType: 'Summary'
    }

    entry.fields.push({
      fieldName: 'clientName',
      fieldLabel: translate('commissions.section.summary.column.payment.reference'),
      fieldValue: transaction.clientname,
      fieldType: 'string'
    });


    entry.fields.push({
      fieldName: 'commission',
      fieldLabel: translate('commissions.section.summary.column.commission'),
      fieldValue: transaction.commission,
      fieldType: 'currency',
      ...(transaction.commissioncurrency !== '' && {
        customAttributes: {
          currency: GetCurrencySymbol(transaction.commissioncurrency)
        }
      })
    });

    entry.fields.push({
      fieldName: 'paid',
      fieldLabel: translate('commissions.section.summary.column.paid'),
      fieldValue: transaction.paid,
      fieldType: 'currency',
      ...(transaction.paidcurrency !== '' && {
        customAttributes: {
          currency: GetCurrencySymbol(transaction.paidcurrency)
        }
      })
    });

    events.push(entry);
  });
}

function createDetailedEventEntry(transactions: CommissionTabularDataType, events: any, translate: Function) {
  transactions.commission_payment_breakdown_detailed_version?.forEach((transaction: any) => {
    const transaction_date: Moment | string = transaction.date !== null ? moment(transaction.date) : '';
    const closed_date: Moment | string = transaction.closeddate !== null ? moment(transaction.closeddate) : '';
    const entry: any = {
      event: {
        tier: transaction.tier,
        eventLabel: transaction_date !== '' ? (transaction_date as Moment).format('DD/MM/YYYY') : ''
      },
      fields: [],
      commissionType: transaction.ismanualcommission ? MANUAL_COMMISSION_PAID_TYPE : AUTO_COMMISSION_PAID_TYPE,
      commissionViewType: 'Detailed'
    };

    entry.fields.push({
      fieldName: 'closed_date',
      fieldLabel: translate('commissions.section.summary.column.close.date'),
      fieldValue: closed_date !== '' ? (closed_date as Moment).format('DD/MM/YYYY') : '',
      fieldType: 'string'
    });


    entry.fields.push({
      fieldName: 'deal_no',
      fieldLabel: translate('commissions.section.summary.column.payment.reference'),
      fieldValue: transaction.dealnumber,
      fieldType: 'string'
    });

    entry.fields.push({
      fieldName: 'uid',
      fieldLabel: translate('commissions.section.summary.column.uid'),
      fieldValue: transaction.uid,
      fieldType: 'string'
    });

    entry.fields.push({
      fieldName: 'turnover',
      fieldLabel: translate('commissions.section.summary.column.turnover'),
      fieldValue: transaction.turnover,
      fieldType: 'currency'
    });

    entry.fields.push({
      fieldName: 'currency_pair',
      fieldLabel: translate('commissions.section.summary.column.currency.pair'),
      fieldValue: transaction.currpair,
      fieldType: 'string'
    });

    entry.fields.push({
      fieldName: 'profit',
      fieldLabel: translate('commissions.section.summary.column.profit'),
      fieldValue: transaction.profit,
      fieldType: 'currency',
      ...(transaction.profitcurrency !== '' && {
        customAttributes: {
          currency: GetCurrencySymbol(transaction.profitcurrency)
        }
      })
    });

    entry.fields.push({
      fieldName: 'commission',
      fieldLabel: translate('commissions.section.summary.column.commission'),
      fieldValue: transaction.commission,
      fieldType: 'currency',
      ...(transaction.commissioncurrency !== '' && {
        customAttributes: {
          currency: GetCurrencySymbol(transaction.commissioncurrency)
        }
      })
    });

    entry.fields.push({
      fieldName: 'paid',
      fieldLabel: translate('commissions.section.summary.column.paid'),
      fieldValue: transaction.paid,
      fieldType: 'currency',
      ...(transaction.paidcurrency !== '' && {
        customAttributes: {
          currency: GetCurrencySymbol(transaction.paidcurrency)
        }
      })
    });

    events.push(entry);
  });
}

export const details = (transactions: CommissionTabularDataType, commissionsViewType: string, translate: Function) => {
  let events: any = [];

  if (commissionsViewType === 'Summary') {
    createSummaryEventEntry(transactions, events, translate)
  }

  if (commissionsViewType === 'Detailed') {
    createDetailedEventEntry(transactions, events, translate)
  }

  const commissionTypeVersionType = commissionsViewType === 'Detailed' ?
    'commission_payment_detail_detailed_version' :
    'commission_payment_detail_summary_version';

  const commissionTypeVersion: CommissionPaymentDetailsType | undefined = transactions?.[commissionTypeVersionType]?.[0];
  const tiersDataObj = commissionsViewType === 'Detailed' ? {
    FirstTierCommissionAmount: numeral(commissionTypeVersion?.firsttier).format('0,0.00'),
    SecondTierCommissionAmount: numeral(commissionTypeVersion?.secondtier).format('0,0.00'),
    ThirdTierCommissionAmount: numeral(commissionTypeVersion?.thirdtier).format('0,0.00'),
    TierAmountCurrency: commissionTypeVersion?.tieramountcurrency
  } : {};

  return {
    events,
    summary: {
      CommPaidDate: moment(commissionTypeVersion?.paiddate).format('DD/MM/YYYY'),
      CommPaidCurrency: commissionTypeVersion?.paidcurrency,
      CommRate: numeral(commissionTypeVersion?.rate).format('0,0.0000'),
      TotalClosedContracts: numeral(commissionTypeVersion?.totalclosecontracts).format('0,0'),
      NewTradingClients: numeral(commissionTypeVersion?.newtradingclients).format('0,0'),
      ...tiersDataObj,
      TotalCommissionAmount: numeral(commissionTypeVersion?.totalcommission).format('0,0.00'),
      TotalCommissionAmountPaid: numeral(commissionTypeVersion?.totalamountpaid).format('0,0.00'),
      TotalCommissionCurrency: commissionTypeVersion?.commissioncurrency
    }
  }
}